export const SW_CLEANUP_VERSION = "v11";

const SW_CLEANUP_RELOAD_KEY = `finplay:sw-cleanup-reloaded:${SW_CLEANUP_VERSION}`;
const MAX_CLEANUP_RELOADS = 2;

type CleanupReason =
  | "boot-unregister"
  | "controllerchange"
  | "debug-clear-cache"
  | "manual"
  | string;

interface CleanupOptions {
  reason?: CleanupReason;
  reload?: boolean;
}

const canUseWindow = () => typeof window !== "undefined";
const canUseServiceWorker = () => typeof navigator !== "undefined" && "serviceWorker" in navigator;
const canUseCacheStorage = () => typeof caches !== "undefined";

export const clearCacheStorage = async (): Promise<string[]> => {
  if (!canUseCacheStorage()) return [];
  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name).catch(() => false)));
    return names;
  } catch {
    return [];
  }
};

const cleanupOldReloadGuards = () => {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("finplay:sw-cleanup-reloaded:") && key !== SW_CLEANUP_RELOAD_KEY) {
        sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Session storage can be unavailable in restricted browser modes.
  }
};

export const reloadAfterCleanup = (reason: CleanupReason = "manual") => {
  if (!canUseWindow()) return false;

  const url = new URL(window.location.href);
  const alreadyOnThisCleanup =
    url.searchParams.get("sw-cleanup") === SW_CLEANUP_VERSION &&
    url.searchParams.get("sw-reason") === reason;

  try {
    const count = Number(sessionStorage.getItem(SW_CLEANUP_RELOAD_KEY) || "0");
    if (count >= MAX_CLEANUP_RELOADS || alreadyOnThisCleanup) return false;
    sessionStorage.setItem(SW_CLEANUP_RELOAD_KEY, String(count + 1));
  } catch {
    if (alreadyOnThisCleanup) return false;
  }

  url.searchParams.set("sw-cleanup", SW_CLEANUP_VERSION);
  url.searchParams.set("sw-version", SW_CLEANUP_VERSION);
  url.searchParams.set("sw-reason", reason);
  window.location.replace(url.toString());
  return true;
};

export const unregisterServiceWorkers = async ({ reason = "manual", reload = true }: CleanupOptions = {}) => {
  const cacheNames = await clearCacheStorage();
  if (!canUseServiceWorker()) return { registrationCount: 0, hadController: false, cacheNames };

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const hadController = Boolean(navigator.serviceWorker.controller) || registrations.length > 0;

    registrations.forEach((registration) => {
      try {
        registration.active?.postMessage({ type: "FINPLAY_PURGE_SW", version: SW_CLEANUP_VERSION });
        registration.waiting?.postMessage({ type: "FINPLAY_PURGE_SW", version: SW_CLEANUP_VERSION });
        registration.installing?.postMessage({ type: "FINPLAY_PURGE_SW", version: SW_CLEANUP_VERSION });
      } catch {
        // Worker state can change while cleanup is running.
      }
    });

    await Promise.all(
      registrations.map(async (registration) => {
        await registration.update().catch(() => undefined);
        await registration.unregister().catch(() => undefined);
      })
    );

    const deletedAfterUnregister = await clearCacheStorage();
    if (reload && hadController) reloadAfterCleanup(reason);

    return {
      registrationCount: registrations.length,
      hadController,
      cacheNames: [...cacheNames, ...deletedAfterUnregister],
    };
  } catch {
    return { registrationCount: 0, hadController: false, cacheNames };
  }
};

export const cleanupStaleServiceWorkersOnBoot = () => {
  if (!canUseWindow()) return;

  let handledControllerChange = false;
  if (canUseServiceWorker()) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (handledControllerChange) return;
      handledControllerChange = true;
      reloadAfterCleanup("controllerchange");
    });
  }

  unregisterServiceWorkers({ reason: "boot-unregister", reload: true }).then(({ registrationCount }) => {
    if (registrationCount === 0) cleanupOldReloadGuards();
  });
};